import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import copy from "copy-to-clipboard";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import { registrableDomain } from "../utils/domain";

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onCopy = () => {
    copy(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Tooltip title={copied ? "Copied" : `Copy ${label}`}>
      <IconButton size="small" onClick={onCopy} aria-label={`Copy ${label}`}>
        {copied ? <CheckIcon fontSize="inherit" sx={{ color: colors.success.main }} /> : <ContentCopyIcon fontSize="inherit" />}
      </IconButton>
    </Tooltip>
  );
}

CopyButton.propTypes = { value: PropTypes.string, label: PropTypes.string.isRequired };
CopyButton.defaultProps = { value: "" };

const cellSx = { fontSize: "0.8125rem", color: colors.text.dark, verticalAlign: "top", py: 1 };
const monoSx = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all" };

function DnsInstructions({ domain, domainKind, records, pendingText }) {
  const zone = domain ? registrableDomain(domain) : "";
  return (
    <MDBox>
      <MDBox p={1.5} mb={1.5} sx={{ borderRadius: 2, bgcolor: colors.background.offWhite, border: `1px solid ${colors.border.default}` }}>
        {domainKind === "apex" ? (
          <MDTypography variant="caption" color="text" display="block">
            <strong>{domain}</strong> is a root (apex) domain. DNS doesn&apos;t allow a CNAME at the root, so add the
            {" "}<strong>A</strong> record below. If your DNS provider supports ALIAS, ANAME or CNAME flattening, you can point
            the root at your PearlCRM address instead. To use <strong>www.{domain}</strong> as well, connect it as a separate subdomain.
          </MDTypography>
        ) : (
          <MDTypography variant="caption" color="text" display="block">
            <strong>{domain || "Your domain"}</strong> is a subdomain, so it points at your PearlCRM address with a
            {" "}<strong>CNAME</strong> record.
          </MDTypography>
        )}
        <MDTypography variant="caption" color="text" display="block" mt={0.75}>
          Add these records at the provider that hosts DNS for <strong>{zone || "your domain"}</strong>. Most providers
          fill in the domain for you, so enter only the part before <strong>{zone ? `.${zone}` : "your domain"}</strong> as
          the name (use <strong>@</strong> for the root). The TXT record proves you own the domain. Changes can take up to an
          hour to show up.
        </MDTypography>
      </MDBox>
      <TableContainer sx={{ border: `1px solid ${colors.border.default}`, borderRadius: 2, overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 600, width: 80 }}>Type</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Name / host</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Value / target</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record, index) => (
              <TableRow key={`${record.type}-${record.name}-${record.value || index}`}>
                <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{record.type}</TableCell>
                <TableCell sx={cellSx}>
                  <MDBox display="flex" alignItems="flex-start" gap={0.5}>
                    <MDBox component="span" sx={monoSx}>{record.name}</MDBox>
                    <CopyButton value={record.name} label="name" />
                  </MDBox>
                </TableCell>
                <TableCell sx={cellSx}>
                  {record.value ? (
                    <MDBox display="flex" alignItems="flex-start" gap={0.5}>
                      <MDBox component="span" sx={monoSx}>{record.value}</MDBox>
                      <CopyButton value={record.value} label="value" />
                    </MDBox>
                  ) : (
                    <MDBox component="span" sx={{ color: colors.text.light, fontStyle: "italic" }}>{pendingText}</MDBox>
                  )}
                  {record.purpose && (
                    <MDTypography variant="caption" color="text" display="block" mt={0.25}>{record.purpose}</MDTypography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </MDBox>
  );
}

DnsInstructions.propTypes = {
  domain: PropTypes.string,
  domainKind: PropTypes.oneOf(["apex", "subdomain", null]),
  records: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.string,
    purpose: PropTypes.string,
  })).isRequired,
  pendingText: PropTypes.string,
};

DnsInstructions.defaultProps = {
  domain: "",
  domainKind: null,
  pendingText: "Shown after you save",
};

export default DnsInstructions;
